/* ==========================================================
   Signed Poster Sale — page-level behaviors
   - <countdown-timer>          : ticking countdown
   - <poster-image-cycler>      : cycle through product images on hover/touch
   - <poster-details-modal>     : "learn more" modal that fetches product JSON
   - Auto-pagination loader     : fetches remaining pages of the all-posters grid
   ========================================================== */

(function () {
  'use strict';

  const prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- COUNTDOWN ---------------- */

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.targetMs = this.resolveTargetMs();
      this.tiles = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    resolveTargetMs() {
      const raw = (this.dataset.endDate || '').trim();
      if (raw) {
        const parsed = new Date(raw);
        if (!isNaN(parsed.getTime())) return parsed.getTime();
      }
      // Default: 7 days from now
      return Date.now() + 7 * 24 * 60 * 60 * 1000;
    }

    tick() {
      const diff = this.targetMs - Date.now();
      if (diff <= 0) {
        this.renderEnded();
        clearInterval(this.interval);
        return;
      }
      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;
      this.update(this.tiles.days, days);
      this.update(this.tiles.hours, hours);
      this.update(this.tiles.minutes, minutes);
      this.update(this.tiles.seconds, seconds, true);
    }

    update(el, value, animate) {
      if (!el) return;
      const next = String(value).padStart(2, '0');
      if (el.textContent === next) return;
      el.textContent = next;
      if (animate && !prefersReducedMotion) {
        el.classList.remove('is-flip');
        // force reflow so animation re-triggers
        void el.offsetWidth;
        el.classList.add('is-flip');
      }
    }

    renderEnded() {
      this.classList.add('is-ended');
      const grid = this.querySelector('.poster-sale__countdown-grid');
      if (grid) {
        grid.innerHTML = '<div class="poster-sale__countdown-ended">Sale has ended</div>';
      }
      const label = this.querySelector('.poster-sale__countdown-label');
      if (label) label.textContent = 'Thanks for the love';
    }
  }

  if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', CountdownTimer);
  }

  /* ---------------- IMAGE CYCLER ---------------- */

  class PosterImageCycler extends HTMLElement {
    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.poster-sale__card-img'));
      if (this.images.length <= 1) return;
      this.index = 0;
      this.interval = null;

      this.onEnter = this.start.bind(this);
      this.onLeave = this.stop.bind(this);

      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('touchstart', this.onEnter, { passive: true });
      this.addEventListener('touchend', this.onLeave);
      this.addEventListener('touchcancel', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (this.interval || this.images.length <= 1) return;
      const step = prefersReducedMotion ? 1500 : 750;
      this.interval = setInterval(() => this.advance(), step);
    }

    stop() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      this.setActive(0);
    }

    advance() {
      const next = (this.index + 1) % this.images.length;
      this.setActive(next);
    }

    setActive(idx) {
      if (this.images[this.index]) this.images[this.index].classList.remove('is-active');
      if (this.images[idx]) this.images[idx].classList.add('is-active');
      this.index = idx;
    }
  }

  if (!customElements.get('poster-image-cycler')) {
    customElements.define('poster-image-cycler', PosterImageCycler);
  }

  /* ---------------- LEARN-MORE MODAL ---------------- */

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    // Simple USD-style fallback
    return '$' + (cents / 100).toFixed(2);
  }

  class PosterDetailsModal extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
    }

    connectedCallback() {
      this.body = this.querySelector('[data-modal-body]');
      this.inner = this.querySelector('.poster-sale__modal-inner');
      this.closeBtn = this.querySelector('.poster-sale__modal-close');

      this.onClose = this.hide.bind(this);
      this.onBackdrop = (e) => {
        if (e.target === this) this.hide();
      };
      this.onKey = (e) => {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'Escape') this.hide();
      };

      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onClose);
      this.addEventListener('click', this.onBackdrop);
      document.addEventListener('keydown', this.onKey);

      // Open the modal whenever anything inside a card with the
      // .poster-sale__open-modal or .poster-sale__learn-more class is clicked.
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest(
          '.poster-sale__learn-more, .poster-sale__open-modal'
        );
        if (!trigger) return;
        if (!trigger.dataset.productHandle) return;
        e.preventDefault();
        this.show(trigger);
      });
    }

    async show(opener) {
      this.opener = opener;
      const handle = opener.dataset.productHandle;
      if (!handle) return;
      this.setAttribute('open', '');
      document.body.classList.add('poster-sale-modal-open');
      this.body.removeAttribute('data-has-product');
      this.body.innerHTML = '<p class="poster-sale__modal-loading">Loading…</p>';
      this.previouslyFocused = document.activeElement;

      try {
        const product = await this.loadProduct(handle);
        this.render(product);
        if (this.inner) this.inner.focus({ preventScroll: true });
      } catch (err) {
        this.body.innerHTML =
          '<p class="poster-sale__modal-loading">Sorry, we couldn\'t load this product. <a href="' +
          opener.dataset.productUrl +
          '">View product page</a>.</p>';
        console.error('[poster-sale] failed to load product', err);
      }
    }

    hide() {
      this.removeAttribute('open');
      document.body.classList.remove('poster-sale-modal-open');
      if (this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus();
      }
    }

    async loadProduct(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const resp = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error('product fetch failed: ' + resp.status);
      const product = await resp.json();
      this.cache.set(handle, product);
      return product;
    }

    pickVariant(product) {
      const available = product.variants.find((v) => v.available);
      return available || product.variants[0];
    }

    render(product) {
      const images = (product.images && product.images.length ? product.images : [product.featured_image]).filter(Boolean);
      const variant = this.pickVariant(product);
      const onSale = variant && variant.compare_at_price && variant.compare_at_price > variant.price;

      const galleryHTML = `
        <div class="poster-sale__modal-gallery">
          <div class="poster-sale__modal-gallery-main" data-gallery-main>
            ${images
              .map(
                (src, i) =>
                  `<img class="poster-sale__modal-gallery-img${i === 0 ? ' is-active' : ''}" src="${src}" alt="${
                    product.title
                  }" data-index="${i}" loading="${i === 0 ? 'eager' : 'lazy'}">`
              )
              .join('')}
            ${
              images.length > 1
                ? `<button type="button" class="poster-sale__modal-gallery-arrow poster-sale__modal-gallery-arrow--prev" data-gallery-prev aria-label="Previous image">‹</button>
                   <button type="button" class="poster-sale__modal-gallery-arrow poster-sale__modal-gallery-arrow--next" data-gallery-next aria-label="Next image">›</button>`
                : ''
            }
          </div>
          ${
            images.length > 1
              ? `<div class="poster-sale__modal-gallery-dots" data-gallery-dots>
                  ${images.map((_, i) => `<button class="poster-sale__modal-gallery-dot${i === 0 ? ' is-active' : ''}" data-index="${i}" aria-label="Image ${i + 1}"></button>`).join('')}
                 </div>`
              : ''
          }
        </div>
      `;

      const priceHTML = onSale
        ? `<span class="poster-sale__modal-details-price-sale">${formatMoney(variant.price)}</span>
           <span class="poster-sale__modal-details-price-compare">${formatMoney(variant.compare_at_price)}</span>`
        : `<span>${formatMoney(variant ? variant.price : product.price)}</span>`;

      const addLabel = variant && variant.available ? 'Add to cart' : 'Sold out';
      const disabled = !variant || !variant.available ? 'disabled' : '';

      this.body.setAttribute('data-has-product', '');
      this.body.innerHTML = `
        ${galleryHTML}
        <div class="poster-sale__modal-details">
          <h2 id="PosterSaleModalTitle">${escapeHTML(product.title)}</h2>
          <div class="poster-sale__modal-details-price">${priceHTML}</div>
          <div class="poster-sale__modal-details-desc">${product.description || ''}</div>
          <div class="poster-sale__modal-details-actions">
            <button
              type="button"
              class="poster-sale__add-btn"
              data-modal-add
              data-variant-id="${variant ? variant.id : ''}"
              ${disabled}
            >
              <span class="poster-sale__add-btn-label">${addLabel}</span>
              <span class="poster-sale__add-btn-spark" aria-hidden="true"></span>
            </button>
            <a class="poster-sale__modal-details-link" href="${product.url}">View full details →</a>
          </div>
        </div>
      `;

      this.bindGallery(images.length);
      this.bindAddToCart();
    }

    bindGallery(count) {
      if (count <= 1) return;
      const imgs = this.body.querySelectorAll('.poster-sale__modal-gallery-img');
      const dots = this.body.querySelectorAll('.poster-sale__modal-gallery-dot');
      let idx = 0;
      const setIdx = (next) => {
        next = (next + count) % count;
        imgs[idx].classList.remove('is-active');
        if (dots[idx]) dots[idx].classList.remove('is-active');
        imgs[next].classList.add('is-active');
        if (dots[next]) dots[next].classList.add('is-active');
        idx = next;
      };
      const prev = this.body.querySelector('[data-gallery-prev]');
      const next = this.body.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => setIdx(idx - 1));
      if (next) next.addEventListener('click', () => setIdx(idx + 1));
      dots.forEach((d) =>
        d.addEventListener('click', () => setIdx(parseInt(d.dataset.index, 10)))
      );
      // Keyboard arrows while modal is open
      this._galleryKey = (e) => {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'ArrowLeft') setIdx(idx - 1);
        if (e.key === 'ArrowRight') setIdx(idx + 1);
      };
      document.addEventListener('keydown', this._galleryKey);
    }

    bindAddToCart() {
      const btn = this.body.querySelector('[data-modal-add]');
      if (!btn) return;
      btn.addEventListener('click', async () => {
        if (btn.disabled) return;
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.classList.add('is-loading');
        try {
          const resp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          });
          if (!resp.ok) throw new Error('add to cart failed');
          await resp.json();
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          const label = btn.querySelector('.poster-sale__add-btn-label');
          if (label) label.textContent = 'Added ✓';
          notifyCart();
          setTimeout(() => {
            btn.classList.remove('is-added');
            if (label) label.textContent = 'Add to cart';
          }, 1800);
        } catch (err) {
          btn.classList.remove('is-loading');
          console.error('[poster-sale] add to cart failed', err);
        }
      });
    }
  }

  if (!customElements.get('poster-details-modal')) {
    customElements.define('poster-details-modal', PosterDetailsModal);
  }

  /* ---------------- CART NOTIFICATION HELPER ---------------- */

  function notifyCart() {
    // Refresh cart drawer / count if the theme exposes one
    try {
      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-icon-bubble');
      if (cart && typeof cart.renderContents === 'function') {
        fetch('/?sections=cart-notification,cart-drawer,cart-icon-bubble')
          .then((r) => r.json())
          .then((data) => cart.renderContents({ sections: data, items: [] }))
          .catch(() => {});
      } else if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'poster-sale' });
      } else {
        // Last-resort: trigger a click on the cart icon to surface it.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (e) {
      /* no-op */
    }
  }

  /* ---------------- 1-CLICK ADD ENHANCEMENTS ---------------- */
  // Provide loading state on card-level add buttons (the theme's <product-form>
  // already handles the fetch + cart drawer refresh via PubSub).
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    const btn = form.querySelector('.poster-sale__add-btn');
    if (!btn) return;
    btn.classList.add('is-loading');
  });

  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      document.querySelectorAll('.poster-sale__add-btn.is-loading').forEach((btn) => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        const label = btn.querySelector('.poster-sale__add-btn-label');
        const original = label ? label.textContent : '';
        if (label) label.textContent = 'Added ✓';
        setTimeout(() => {
          btn.classList.remove('is-added');
          if (label && original) label.textContent = original;
        }, 1800);
      });
    });
  }

  // Fallback: clear loading after a hard ceiling in case PubSub never fires
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    setTimeout(() => {
      form.querySelectorAll('.poster-sale__add-btn.is-loading').forEach((btn) => {
        btn.classList.remove('is-loading');
      });
    }, 4000);
  });

  /* ---------------- AUTO-PAGINATION LOADER ---------------- */

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async function loadRemainingPages() {
    const grid = document.querySelector('.poster-sale__grid--all');
    if (!grid) return;
    const handle = grid.dataset.collectionHandle;
    const loadMore = document.querySelector('[data-loadmore]');
    const meta = grid.querySelector('[data-poster-sale-pagination]');
    if (!handle || !meta) return;
    const total = parseInt(meta.dataset.totalPages, 10);
    const current = parseInt(meta.dataset.currentPage, 10);
    if (!total || total <= current) return;

    if (loadMore) loadMore.hidden = false;

    for (let page = current + 1; page <= total; page++) {
      try {
        const resp = await fetch(`/collections/${handle}?page=${page}&view=poster-sale-fragment`, {
          credentials: 'same-origin',
        });
        // Theme may not have a `poster-sale-fragment` view alternate; the
        // default collection page works as long as we can extract product
        // cards by URL. Strategy: parse and find product anchors.
        const html = await resp.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Collect unique product handles from anchor hrefs.
        const handles = Array.from(doc.querySelectorAll('a[href^="/products/"]'))
          .map((a) => {
            const m = a.getAttribute('href').match(/^\/products\/([^/?#]+)/);
            return m ? m[1] : null;
          })
          .filter(Boolean);
        const unique = Array.from(new Set(handles));
        // Filter to handles not already rendered on the page.
        const already = new Set(
          Array.from(document.querySelectorAll('.poster-sale__learn-more')).map(
            (b) => b.dataset.productHandle
          )
        );
        const fresh = unique.filter((h) => !already.has(h));

        // For each fresh handle, fetch product.js and render a minimal card.
        const cards = await Promise.all(fresh.map(buildCardForHandle));
        cards.filter(Boolean).forEach((node) => grid.appendChild(node));
      } catch (err) {
        console.warn('[poster-sale] page', page, 'failed', err);
      }
    }

    if (loadMore) loadMore.hidden = true;
  }

  async function buildCardForHandle(handle) {
    try {
      const resp = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
      });
      if (!resp.ok) return null;
      const p = await resp.json();
      return cardFromProduct(p);
    } catch {
      return null;
    }
  }

  function cardFromProduct(p) {
    const wrap = document.createElement('poster-image-cycler');
    const imgs = (p.images && p.images.length ? p.images : [p.featured_image]).filter(Boolean);
    wrap.className = 'poster-sale__card' + (imgs.length > 1 ? ' poster-sale__card--multi' : '');
    wrap.dataset.imageCount = imgs.length;

    const variant = p.variants.find((v) => v.available) || p.variants[0];
    const onSale = variant && variant.compare_at_price && variant.compare_at_price > variant.price;
    const priceHTML = onSale
      ? `<span class="price__sale"><span class="price-item--sale">${formatMoney(variant.price)}</span>
         <s class="price-item--regular">${formatMoney(variant.compare_at_price)}</s></span>`
      : `<span class="price-item--regular">${formatMoney(variant ? variant.price : p.price)}</span>`;

    wrap.innerHTML = `
      <a href="${p.url}" class="poster-sale__card-media poster-sale__open-modal" aria-label="${escapeHTML(p.title)}" data-product-handle="${p.handle}" data-product-url="${p.url}" tabindex="-1">
        ${imgs
          .slice(0, 6)
          .map(
            (src, i) =>
              `<img class="poster-sale__card-img${i === 0 ? ' is-active' : ''}" src="${src}" alt="${escapeHTML(p.title)}" loading="lazy" data-index="${i}">`
          )
          .join('')}
        ${onSale ? '<span class="poster-sale__badge poster-sale__badge--sale">Sale</span>' : ''}
        ${!variant || !variant.available ? '<span class="poster-sale__badge poster-sale__badge--soldout">Sold out</span>' : ''}
      </a>
      <div class="poster-sale__card-body">
        <h3 class="poster-sale__card-title"><a href="${p.url}" class="poster-sale__card-link poster-sale__open-modal" data-product-handle="${p.handle}" data-product-url="${p.url}">${escapeHTML(p.title)}</a></h3>
        <div class="poster-sale__card-price">${priceHTML}</div>
        <form class="poster-sale__card-form" method="post" action="/cart/add" data-type="add-to-cart-form" data-fallback-add="true">
          <input type="hidden" name="id" value="${variant ? variant.id : ''}" ${!variant || !variant.available ? 'disabled' : ''}>
          <button type="submit" class="poster-sale__add-btn" ${!variant || !variant.available ? 'disabled' : ''}>
            <span class="poster-sale__add-btn-label">${variant && variant.available ? 'Add to cart' : 'Sold out'}</span>
            <span class="poster-sale__add-btn-spark" aria-hidden="true"></span>
          </button>
        </form>
        <button type="button" class="poster-sale__learn-more" data-product-handle="${p.handle}" data-product-url="${p.url}" aria-haspopup="dialog" aria-controls="PosterSaleDetailsModal">Learn more</button>
      </div>
    `;
    // Dynamically rendered forms aren't wrapped in <product-form>, so we
    // submit them via fetch ourselves.
    return wrap;
  }

  // Submit handler for dynamically rendered fallback forms.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches || !form.matches('form[data-fallback-add="true"]')) return;
    e.preventDefault();
    const btn = form.querySelector('.poster-sale__add-btn');
    const idInput = form.querySelector('input[name="id"]');
    if (!btn || !idInput || idInput.disabled) return;
    btn.classList.add('is-loading');
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: idInput.value, quantity: 1 }] }),
      });
      if (!resp.ok) throw new Error('add failed');
      await resp.json();
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      const label = btn.querySelector('.poster-sale__add-btn-label');
      if (label) label.textContent = 'Added ✓';
      notifyCart();
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (label) label.textContent = 'Add to cart';
      }, 1800);
    } catch (err) {
      btn.classList.remove('is-loading');
      console.error('[poster-sale] fallback add failed', err);
    }
  });

  // Kick off auto-pagination on DOM ready (or now if already ready).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadRemainingPages);
  } else {
    loadRemainingPages();
  }
})();
